import useWidgetViewManager from '@/composables/widget-view-manager'
import { useWorkspace } from '@/composables/workflow'
import { captureException } from '@sentry/vue'
import type { AxiosError, AxiosInstance, CreateAxiosDefaults, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'

function addInterceptors(axiosInstance: AxiosInstance) {
    axiosInstance.interceptors.request.use((_request) => {
        const request = {
            ..._request,
            headers: {
                ..._request.headers,
            },
        } as InternalAxiosRequestConfig

        const { token, workflowId, localeHeader } = useWorkspace()

        const { activeMessage } = useWidgetViewManager()
        if (token.value) {
            request.headers.Authorization = `Bearer ${token.value}`
        }

        if (workflowId.value) {
            request.headers['workflow-id'] = workflowId.value
        }

        if (activeMessage.value?.taskId) {
            request.headers['task-id'] = activeMessage.value.taskId
        }

        if (localeHeader.value) {
            request.headers['x-locale'] = localeHeader.value
        }

        return request
    })

    // Add a response interceptor to catch 500 errors
    axiosInstance.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
            if (error.response && error.response.status === 500) {
                captureException(error.message, scope => {
                    scope.setExtras({
                        url: error.config?.url,
                        method: error.config?.method,
                        status: error.response?.status,
                    })

                    return scope
                })
            }

            return Promise.reject(error)
        },
    )

    // handle responses to forbidden requests
    axiosInstance.interceptors.response.use(
        (res) => {
            const expectedJson = (res.config.headers.get('accept') as string | undefined)?.includes('application/json')
            const receivedHtml = (res.headers['content-type'] as string | undefined)?.includes('text/html')

            // due to config issues, CloudFront can swallow proper 40x error and replace it with 200 with HTML
            if (expectedJson && receivedHtml) {
                useWidgetViewManager().showTimeoutError()

                captureException('Expected JSON, received HTML', scope => {
                    scope.setExtras({
                        url: res.config.url,
                        method: res.config.method,
                        status: res.status,
                    })

                    return scope
                })

                return Promise.reject({
                    status: 403,
                    response: {
                        status: 403,
                    },
                })
            }

            return res
        },
        (error) => {
            if (error.response && error.response.status === 403) {
                useWidgetViewManager().showTimeoutError()
            }
            return Promise.reject(error)
        },
    )
}

function createApiClient(config?: CreateAxiosDefaults): AxiosInstance {
    const axiosInstance = axios.create(config)
    addInterceptors(axiosInstance)
    return axiosInstance
}

export default createApiClient
